import React, { useState } from "react";
import { Link } from "react-router-dom";

interface CurrencyCalculatorProps {}

const PPPCalculator: React.FC<CurrencyCalculatorProps> = () => {
  const [amount, setAmount] = useState<string>("");
  const [customExchangeRate, setCustomExchangeRate] = useState<string>("");
  const [isUsdToNaira, setIsUsdToNaira] = useState<boolean>(true);
  const [yearlyTarget, setYearlyTarget] = useState<string>("");

  // PPP calculation: Nigeria's purchasing power is roughly 1/13th of US
  const pppMultiplier: number = 1 / 13;
  const defaultExchangeRate: number = 1545;
  const exchangeRate: number =
    parseFloat(customExchangeRate) || defaultExchangeRate;

  const calculateFromUsd = (usd: number): { ppp: number; nominal: number } => {
    if (!usd || usd <= 0) return { ppp: 0, nominal: 0 };
    return {
      ppp: Math.round(usd * exchangeRate * pppMultiplier),
      nominal: Math.round(usd * exchangeRate),
    };
  };

  const calculateFromNaira = (
    naira: number
  ): { ppp: number; nominal: number } => {
    if (!naira || naira <= 0) return { ppp: 0, nominal: 0 };
    return {
      ppp: Math.round((naira / pppMultiplier / exchangeRate) * 100) / 100,
      nominal: Math.round((naira / exchangeRate) * 100) / 100,
    };
  };

  const inputAmount = parseFloat(amount) || 0;
  const results = isUsdToNaira
    ? calculateFromUsd(inputAmount)
    : calculateFromNaira(inputAmount);

  // Financial target calculations
  const target = parseFloat(yearlyTarget) || 0;
  const servicePrice = isUsdToNaira ? results.ppp : inputAmount;

  const calculateTargets = () => {
    if (!target || !servicePrice || servicePrice <= 0) return null;

    const salesNeeded = Math.ceil(target / servicePrice);
    return {
      yearly: salesNeeded,
      monthly: Math.ceil(salesNeeded / 12),
      weekly: Math.ceil(salesNeeded / 52),
      daily: Math.ceil(salesNeeded / 365),
    };
  };

  const targetBreakdown = calculateTargets();

  const handleSwitch = (): void => {
    setIsUsdToNaira(!isUsdToNaira);
    setAmount("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Service Pricing & Target Calculator
          </h1>
          <p className="text-gray-600">
            Get PPP-adjusted pricing and calculate sales targets
          </p>
        </div>

        <div className="space-y-6">
          {/* Currency Switch */}
          <div className="text-center">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conversion Direction
            </label>
            <button
              onClick={handleSwitch}
              className="flex items-center space-x-2 bg-blue-100 hover:bg-blue-200 px-6 py-3 rounded-lg transition-colors duration-200 mx-auto"
            >
              <span className="font-medium text-blue-800">
                {isUsdToNaira ? "USD → NGN" : "NGN → USD"}
              </span>
              <svg
                className="w-4 h-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                />
              </svg>
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Click to switch conversion direction
            </p>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isUsdToNaira
                ? "Service Price in USD ($)"
                : "Service Price in Naira (₦)"}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">
                {isUsdToNaira ? "$" : "₦"}
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAmount(e.target.value)
                }
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg"
                placeholder={`Enter your service price`}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Yearly Target Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yearly Financial Target (₦)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">
                ₦
              </span>
              <input
                type="number"
                value={yearlyTarget}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setYearlyTarget(e.target.value)
                }
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg"
                placeholder="Enter your yearly target"
                min="0"
                step="1000"
              />
            </div>
          </div>

          {/* Exchange Rate Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exchange Rate (₦/$) - Optional
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                ₦
              </span>
              <input
                type="number"
                value={customExchangeRate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCustomExchangeRate(e.target.value)
                }
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg"
                placeholder={`Default: ${defaultExchangeRate}`}
                min="0"
                step="0.01"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Current rate: ₦{exchangeRate.toLocaleString()} per $1
            </p>
          </div>

          {/* Pricing Results */}
          {amount && inputAmount > 0 && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-800 text-lg">
                Pricing Analysis
              </h3>

              {isUsdToNaira ? (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      Recommended Price (PPP-Adjusted)
                    </h4>
                    <p className="text-2xl font-bold text-green-700">
                      ₦{results.ppp.toLocaleString()}
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      Same purchasing power as ${amount} in the US
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <span className="w-3 h-3 bg-gray-400 rounded-full mr-2"></span>
                      Direct Conversion (Not Recommended)
                    </h4>
                    <p className="text-xl font-semibold text-gray-600">
                      ₦{results.nominal.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Too expensive for local market
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      PPP Equivalent in USD
                    </h4>
                    <p className="text-2xl font-bold text-green-700">
                      ${results.ppp.toFixed(2)}
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      ₦{amount} has this purchasing power in the US
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <span className="w-3 h-3 bg-gray-400 rounded-full mr-2"></span>
                      Direct Conversion
                    </h4>
                    <p className="text-xl font-semibold text-gray-600">
                      ${results.nominal.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Market exchange rate only
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Sales Target Breakdown */}
          {targetBreakdown && servicePrice > 0 && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-800 text-lg">
                Sales Targets to Hit ₦
                {parseFloat(yearlyTarget).toLocaleString()}
              </h3>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-3">
                  At ₦{servicePrice.toLocaleString()} per service
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">
                      {targetBreakdown.yearly}
                    </p>
                    <p className="text-sm text-purple-600">Sales/Year</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">
                      {targetBreakdown.monthly}
                    </p>
                    <p className="text-sm text-purple-600">Sales/Month</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">
                      {targetBreakdown.weekly}
                    </p>
                    <p className="text-sm text-purple-600">Sales/Week</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">
                      {targetBreakdown.daily}
                    </p>
                    <p className="text-sm text-purple-600">Sales/Day</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
            <h4 className="font-semibold text-blue-800 mb-1">
              How this works:
            </h4>
            <p className="text-blue-700">
              {isUsdToNaira
                ? "This calculator adjusts for Nigeria's purchasing power (1/13th of US) to help you price services appropriately for the local market, then calculates how many sales you need to hit your financial targets."
                : "This shows what your Naira amount is worth in US purchasing power terms, considering the economic differences between both countries, plus your sales targets."}
            </p>
          </div>
        </div>
         {/* Promotional Section */}
        <div className="mt-8 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-3">
            Want to Make Money Selling Your Knowledge in 2026?
          </h2>
          <p className="text-lg text-green-50 mb-6 max-w-3xl mx-auto">
            This is how busy salary earners are making at least ₦1M naira
            extra income without quitting their jobs.
          </p>
          <Link
            to="/dbc/free-training"
            className="inline-block bg-white text-green-700 font-semibold px-8 py-4 rounded-lg hover:bg-green-50 transition-colors shadow-lg text-lg"
          >
            Click Here
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PPPCalculator;
